import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, BookOpen, ChevronUp, ChevronDown, Layers, Users, Settings, Plus, Trash2, X, Edit2, Info, Calculator, ShieldCheck, TrendingUp, BarChart2, Activity, Target } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { unescapeHTML } from '../../utils/sanitize';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

const UploadCGPA = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollegeId, setSelectedCollegeId] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const [selectedBatchForUpload, setSelectedBatchForUpload] = useState(null); // { degree, batch }
  const [batchStudents, setBatchStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const [selectedBatchForView, setSelectedBatchForView] = useState(null); // { degree, batch }
  const [viewStudents, setViewStudents] = useState([]);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [activeSemesterTab, setActiveSemesterTab] = useState(null);
  
  // Filtering States
  const [searchTerm, setSearchTerm] = useState('');
  const [backlogFilter, setBacklogFilter] = useState('all'); // 'all', 'clear', 'backlogs'
  const [cgpaFilter, setCgpaFilter] = useState('all');
  const [semesterFilter, setSemesterFilter] = useState('all');


  // Edit State
  const [editingRecord, setEditingRecord] = useState(null);
  const [savingGrades, setSavingGrades] = useState(false);

  // Wizard State
  const [wizardStep, setWizardStep] = useState(1); // 1: Setup, 2: Upload/Preview
  const [semester, setSemester] = useState('1');
  const [method, setMethod] = useState('slab');
  const [subjects, setSubjects] = useState([{ code: '', name: '', credits: '' }]);
  const [includeArrears, setIncludeArrears] = useState(false);
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [smartPasteText, setSmartPasteText] = useState('');
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  // Grade Mapping Configuration
  const [showGradeMappingModal, setShowGradeMappingModal] = useState(false);
  const [gradeMapping, setGradeMapping] = useState({
    'O': 10,
    'A+': 9,
    'A': 8,
    'B+': 7,
    'B': 6,
    'C': 5,
    'D': 4,
    'E': 3
  });
  
  const [arrearGrades, setArrearGrades] = useState([
    'RA', 'U', 'F', 'AB', 'W', 'SA'
  ]);

  // Shared Filtered Students State (Used by Directory & Analytics)
  const filteredStudents = React.useMemo(() => {
    if (!viewStudents) return [];
    return viewStudents.filter(student => {
      // Search Term Match
      const search = searchTerm.toLowerCase();
      const matchesSearch = student.fullName?.toLowerCase().includes(search) || 
                            student.studentId?.toLowerCase().includes(search);
      if (!matchesSearch) return false;

      // Backlog Filter Match
      const hasBacklogs = student.activeArrears && student.activeArrears.length > 0;
      if (backlogFilter === 'clear' && hasBacklogs) return false;
      if (backlogFilter === 'backlogs' && !hasBacklogs) return false;

      // Semester Filter Match
      if (semesterFilter !== 'all') {
        const hasSem = student.academicRecords?.some(r => r.semester === Number(semesterFilter));
        if (!hasSem) return false;
      }

      // CGPA Filter Match
      if (cgpaFilter !== 'all') {
        const latestRecord = student.academicRecords && student.academicRecords.length > 0 
          ? student.academicRecords[student.academicRecords.length - 1] 
          : null;
        
        if (!latestRecord || !latestRecord.cgpa) return false;
        
        const cgpa = latestRecord.cgpa;
        if (cgpaFilter === '9+' && cgpa < 9.0) return false;
        if (cgpaFilter === '8-9' && (cgpa < 8.0 || cgpa >= 9.0)) return false;
        if (cgpaFilter === '7-8' && (cgpa < 7.0 || cgpa >= 8.0)) return false;
        if (cgpaFilter === '6-7' && (cgpa < 6.0 || cgpa >= 7.0)) return false;
        if (cgpaFilter === '<6' && cgpa >= 6.0) return false;
      }

      return true;
    });
  }, [viewStudents, searchTerm, backlogFilter, semesterFilter, cgpaFilter]);


  // Upload/Preview state
  const [parsedResults, setParsedResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/colleges', { params: { limit: 9999 } });
      if (data.success) {
        setColleges(data.data);
        if (data.data.length === 1) {
          setSelectedCollegeId(data.data[0]._id);
        } else if (user?.college) {
          setSelectedCollegeId(typeof user.college === 'string' ? user.college : user.college._id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
      toast.error('Failed to load institution data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBatchForUpload) {
      fetchBatchStudents();
      // Reset wizard
      setWizardStep(1);
      setParsedResults(null);
      setSubjects([{ code: '', name: '', credits: '' }]);
    }
  }, [selectedBatchForUpload]);

  const isStudentInDegreeAndBatch = (student, targetDegree, targetBatch, targetDeptId) => {
    // Check batch match
    const studentBatch = student.batch || student.department?.batch?.batch || (typeof student.department?.batch === 'string' ? student.department?.batch : '');
    if (targetBatch && studentBatch && studentBatch !== targetBatch) {
      return false;
    }

    const normalize = (str) => String(str || '').trim().toLowerCase();
    const target = normalize(targetDegree);

    // 1. Direct ID match if deptId available
    if (targetDeptId) {
      const studentDeptId = student.department?._id || student.department?.degreeId || student.degree?._id || student.degree;
      if (studentDeptId && studentDeptId.toString() === targetDeptId.toString()) {
        return true;
      }
    }

    // 2. Structured Department Match
    if (student.department && typeof student.department === 'object') {
      const dept = student.department;
      const degreeAbbr = unescapeHTML(dept.abbreviation || dept.fullName || '');
      const degreeFull = unescapeHTML(dept.fullName || dept.name || '');
      const specStr = Array.isArray(dept.specialization)
        ? dept.specialization.map(s => unescapeHTML(s)).filter(s => s && s !== 'General').join(', ')
        : (dept.specialization && dept.specialization !== 'General' ? unescapeHTML(dept.specialization) : '');
      const spec = specStr ? ` in ${specStr}` : '';
      
      const friendlyNameAbbr = normalize(`${degreeAbbr}${spec}`);
      const friendlyNameFull = normalize(`${degreeFull}${spec}`);

      if (friendlyNameAbbr && (friendlyNameAbbr === target || target.includes(friendlyNameAbbr))) {
        return true;
      }
      if (friendlyNameFull && (friendlyNameFull === target || target.includes(friendlyNameFull))) {
        return true;
      }
    } else if (typeof student.department === 'string' && student.department.trim()) {
      const deptStr = normalize(unescapeHTML(student.department));
      if (deptStr === target || target.includes(deptStr) || deptStr.includes(target)) {
        return true;
      }
    }

    // 3. Fallback for legacy academic object (only if degreeGroup is present)
    if (student.academic && student.academic.degreeGroup) {
      const legacyAbbr = unescapeHTML(student.academic.degreeGroup);
      const legacySpec = unescapeHTML(student.academic.specialisation || '');
      const legacyName = normalize(legacySpec && legacySpec !== 'General' ? `${legacyAbbr} in ${legacySpec}` : legacyAbbr);
      if (legacyName && (legacyName === target || target.includes(legacyName))) {
        return true;
      }
    }

    return false;
  };

  const fetchBatchStudents = async () => {
    try {
      setLoadingStudents(true);
      const params = {
        role: 'student',
        college: selectedCollegeId,
        batch: selectedBatchForUpload.batch,
        limit: 9999
      };
      
      const { data } = await api.get('/users', { params });
      
      if (data.success) {
        const filteredStudents = (data.data || []).filter(student =>
          isStudentInDegreeAndBatch(
            student,
            selectedBatchForUpload.degree,
            selectedBatchForUpload.batch,
            selectedBatchForUpload.deptId
          )
        );
        setBatchStudents(filteredStudents);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Failed to load students for this batch');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleViewRecords = async (degree, batch, deptId) => {
    setSelectedBatchForView({ degree, batch, deptId });
    try {
      setLoadingStudents(true);
      const params = {
        role: 'student',
        college: selectedCollegeId,
        batch: batch,
        limit: 9999
      };
      
      const { data } = await api.get('/users', { params });
      
      if (data.success) {
        const filteredStudents = (data.data || []).filter(student =>
          isStudentInDegreeAndBatch(student, degree, batch, deptId)
        );
        setViewStudents(filteredStudents);
      }
    } catch (error) {
      toast.error('Failed to load students for this batch');
    } finally {
      setLoadingStudents(false);
    }
  };

  const selectedCollege = colleges.find(c => c._id === selectedCollegeId);

  const getGroupedDepartments = (departments) => {
    if (!departments || departments.length === 0) return [];
    return departments.map(dept => {
      const degreeAbbr = unescapeHTML(dept.abbreviation || dept.fullName || 'Degree');
      const specStr = Array.isArray(dept.specialization)
        ? dept.specialization.map(s => unescapeHTML(s)).filter(s => s && s !== 'General').join(', ')
        : (dept.specialization && dept.specialization !== 'General' ? unescapeHTML(dept.specialization) : '');
      const spec = specStr ? ` in ${specStr}` : '';
      const friendlyName = `${degreeAbbr}${spec}`;
      
      const batches = dept.batches?.map(b => b.batch) || [];
      const uniqueBatches = [...new Set(batches)].sort((a, b) => b.localeCompare(a));
      
      let finalDuration = dept.duration || 'N/A';
      if (uniqueBatches.length > 0) {
        const sampleBatch = uniqueBatches[0];
        if (sampleBatch && sampleBatch.includes('-')) {
          const [start, end] = sampleBatch.split('-');
          if (!isNaN(start) && !isNaN(end)) {
            finalDuration = `${Number(end) - Number(start)} Years`;
          }
        }
      }
      
      return { 
        _id: dept._id, 
        degreeId: dept.degreeId,
        friendlyName, 
        duration: finalDuration,
        batches: uniqueBatches 
      };
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleEditSemester = (studentId, semesterRecord) => {
    const method = semesterRecord.method || 'slab';
    const recalculatedSubjects = semesterRecord.subjects.map(sub => {
      let pts = sub.pointsEarned;
      if (method === 'slab') {
         const g = sub.grade?.toUpperCase();
         pts = getPointsFromGrade(g) * sub.credits;
      }
      return {
        ...sub,
        pointsEarned: pts
      };
    });

    setEditingRecord({
      studentId,
      semester: semesterRecord.semester,
      subjects: recalculatedSubjects
    });
  };

  const handleGradeChange = (subjectCode, newGrade) => {
    if (!editingRecord) return;
    
    const calculatePoints = (grade, credits) => {
      // Use the dynamic grade mapping!
      const g = grade?.toUpperCase();
      const points = getPointsFromGrade(g);
      return points * credits;
    };

    setEditingRecord(prev => {
      const updatedSubjects = prev.subjects.map(sub => {
        if (sub.code === subjectCode) {
          return {
            ...sub,
            grade: newGrade.toUpperCase(),
            pointsEarned: calculatePoints(newGrade, sub.credits)
          };
        }
        return sub;
      });
      return { ...prev, subjects: updatedSubjects };
    });
  };

  const handleSaveSemester = async () => {
    if (!editingRecord) return;
    setSavingGrades(true);
    try {
      const res = await api.put('/colleges/update-semester-grades', editingRecord);
      if (res.data.success) {
        toast.success(`Semester ${editingRecord.semester} grades saved!`);
        // Update local viewStudents array with the updated student object
        setViewStudents(prev => prev.map(s => s._id === editingRecord.studentId ? res.data.data : s));
        setEditingRecord(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save grades');
    } finally {
      setSavingGrades(false);
    }
  };

  const handleSubjectChange = (index, field, value) => {
    const newSubjects = [...subjects];
    newSubjects[index][field] = value;
    setSubjects(newSubjects);
  };

  const addSubject = () => setSubjects([...subjects, { code: '', name: '', credits: '' }]);
  const removeSubject = (index) => setSubjects(subjects.filter((_, i) => i !== index));

  const handleSmartPaste = () => {
    if (!smartPasteText.trim()) {
      toast.error('Please paste some text first.');
      return;
    }

    const lines = smartPasteText.split('\n');
    const newSubjects = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.toLowerCase().includes('course id') || trimmed.includes('---')) return;

      let parts = [];
      if (trimmed.includes('|')) {
        parts = trimmed.split('|').map(s => s.trim()).filter(Boolean);
      } else if (trimmed.includes('\t')) {
        parts = trimmed.split('\t').map(s => s.trim()).filter(Boolean);
      }

      if (parts.length >= 3) {
        let code = parts[0];
        let name = parts[1];
        let credits = parts[2];

        // if parts[1] is a number and parts[2] is string, swap
        if (!isNaN(parseFloat(parts[1])) && isNaN(parseFloat(parts[2]))) {
          credits = parts[1];
          name = parts[2];
        }

        newSubjects.push({ code, name, credits: parseFloat(credits) || '' });
      } else {
        // Fallback regex: ^([A-Z0-9-]+)\s+(.+?)\s+([\d.]+)$
        const match = trimmed.match(/^([a-zA-Z0-9-]+)\s+(.+?)\s+([\d.]+)$/);
        if (match) {
           newSubjects.push({
             code: match[1].trim(),
             name: match[2].trim().replace(/^[-:|]+|[-:|]+$/g, '').trim(),
             credits: parseFloat(match[3]) || ''
           });
        }
      }
    });

    if (newSubjects.length > 0) {
      const filteredExisting = subjects.filter(s => s.code || s.name || s.credits);
      setSubjects([...filteredExisting, ...newSubjects]);
      setSmartPasteText('');
      setShowSmartPaste(false);
      toast.success(`Successfully parsed ${newSubjects.length} subjects!`);
    } else {
      toast.error('Could not parse subjects. Please check the format.');
    }
  };

  const generateTemplate = () => {
    // Validate
    if (subjects.some(s => !s.code || (method !== 'equal' && !s.credits))) {
      toast.error(method === 'equal' ? "Please fill in all subject codes." : "Please fill in all subject codes and credits.");
      return;
    }

    const wsData = [];
    const headers = ['Student Name', 'SMAART ID', 'Roll Number'];

    const suffix = method === 'slab' ? 'Grade' : method === 'continuous' ? 'Marks' : 'Points';
    
    subjects.forEach(sub => {
      headers.push(`${sub.code} - ${sub.name} (${suffix})`);
    });

    let arrearColumns = [];
    if (includeArrears) {
      const allArrears = new Map();
      batchStudents.forEach(student => {
        if (student.activeArrears) {
          student.activeArrears.forEach(a => allArrears.set(a.subjectCode, a.subjectName || a.subjectCode));
        }
      });
      arrearColumns = Array.from(allArrears.keys());
      arrearColumns.forEach(code => headers.push(`${code} - ${allArrears.get(code)} (Arrear)`));
    }

    wsData.push(headers);

    batchStudents.forEach(student => {
      const row = [
        student.fullName,
        student.studentId || '',
        student.rollNumber || ''
      ];
      // Empty slots for subjects
      subjects.forEach(() => row.push(''));
      // Empty slots for arrears
      arrearColumns.forEach(() => row.push(''));
      wsData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Auto-adjust column widths for a neat Excel format
    const colWidths = headers.map(header => ({
      wch: Math.min(Math.max(header.length + 5, 20), 50)
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CGPA_Template");
    XLSX.writeFile(wb, `CGPA_Template_Sem${semester}_${selectedBatchForUpload.batch}.xlsx`);
    
    setWizardStep(2);
    toast.success("Template Generated! Please fill it and upload.");
  };

  const getPointsFromGrade = (gradeStr) => {
    const upperGrade = String(gradeStr).toUpperCase().trim();
    if (gradeMapping[upperGrade] !== undefined) {
      return gradeMapping[upperGrade];
    }
    if (arrearGrades.includes(upperGrade)) {
      return 0;
    }
    return undefined; // Not configured!
  };

  const isFailure = (gradeStr) => {
    const upperGrade = String(gradeStr).toUpperCase().trim();
    return arrearGrades.includes(upperGrade);
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (!jsonData || jsonData.length === 0) {
          toast.error("File is empty.");
          return;
        }

        const suffix = method === 'slab' ? 'Grade' : method === 'continuous' ? 'Marks' : 'Points';
        const parsed = [];
        const seenStudentIds = new Set();
        let validationError = null;

        // Pre-calculate all active arrear subject codes for this batch
        const allBatchArrearCodes = new Set();
        batchStudents.forEach(s => {
          if (s.activeArrears) {
            s.activeArrears.forEach(a => allBatchArrearCodes.add(a.subjectCode.trim().toUpperCase()));
          }
        });

        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          const studentId = row['SMAART ID'];
          if (!studentId) continue;
          
          if (seenStudentIds.has(studentId)) {
            validationError = `❌ Duplicate student record found in Excel | Student: ${studentId}`;
            break;
          }
          seenStudentIds.add(studentId);

          const student = batchStudents.find(s => s.studentId === studentId);
          if (!student) {
            validationError = `❌ Unknown Student ID in Excel | Student: ${studentId}`;
            break;
          }

          let totalCreditsAttempted = 0;
          let totalPointsEarned = 0;
          let status = 'Pass';
          const studentSubjects = [];
          const clearedArrears = [];
          const seenSubjectCodes = new Set();

          // 1. Normal Subjects
          for (let j = 0; j < subjects.length; j++) {
            const sub = subjects[j];
            
            // Flexible column matching (in case they switch methods and use an old template)
            let val = undefined;
            const expectedPrefix = `${sub.code} - ${sub.name}`;
            for (const key of Object.keys(row)) {
              if (key.startsWith(expectedPrefix)) {
                val = row[key];
                break;
              }
            }
            
            const subCodeUpper = sub.code.trim().toUpperCase();
            const isArrearSubject = allBatchArrearCodes.has(subCodeUpper);
            const studentHasArrear = student.activeArrears?.some(a => a.subjectCode.trim().toUpperCase() === subCodeUpper);

            if (val === undefined || val === '') {
              if (isArrearSubject) {
                // If it's an arrear subject and the cell is blank, the student is not clearing it. Ignore.
                continue;
              } else {
                validationError = `❌ Missing required subject data | Student: ${studentId} | Subject: ${sub.code}`;
                break;
              }
            }
            
            if (isArrearSubject && !studentHasArrear) {
              validationError = `❌ Student does not have this active arrear | Student: ${studentId} | Subject: ${sub.code}`;
              break;
            }
            
            if (Number(sub.credits) < 0) {
              validationError = `❌ Negative credits found | Subject: ${sub.code}`;
              break;
            }
            
            if (seenSubjectCodes.has(sub.code)) {
              validationError = `❌ Duplicate subject code found | Student: ${studentId} | Subject: ${sub.code}`;
              break;
            }
            seenSubjectCodes.add(sub.code);

            const creds = Number(sub.credits);
            let pts = 0;
            let isFail = false;
            const strVal = String(val).trim().toUpperCase();

            if (method === 'slab') {
              if (gradeMapping[strVal] === undefined && !arrearGrades.includes(strVal)) {
                validationError = `❌ Invalid/Unconfigured Grade "${val}" | Student: ${studentId} | Subject: ${sub.code}. Please add it to Grade Mapping first!`;
                break;
              }
              pts = getPointsFromGrade(strVal);
              isFail = isFailure(strVal);
            } else {
              const marks = Number(val);
              if (isNaN(marks) || marks < 0 || marks > 100) {
                validationError = `❌ Invalid Marks "${val}" (Must be 0-100) | Student: ${studentId} | Subject: ${sub.code}`;
                break;
              }
              if (method === 'continuous') {
                pts = marks > 10 ? marks / 10 : marks;
                isFail = pts < 5;
              } else if (method === 'equal') {
                pts = marks;
                isFail = pts < 50; // assuming equal weightage passing mark is 50
              }
            }

            totalCreditsAttempted += (method === 'equal' ? 1 : creds);
            if (!isFail) {
              totalPointsEarned += Number((pts * (method === 'equal' ? 1 : creds)).toFixed(2));
              if (isArrearSubject) {
                clearedArrears.push(sub.code);
                if (status === 'Pass') status = 'Arrear Cleared';
              }
            } else {
              status = 'Has Arrears';
            }

            studentSubjects.push({
              code: sub.code,
              name: sub.name,
              credits: creds,
              grade: strVal,
              pointsEarned: Number((pts * (method === 'equal' ? 1 : creds)).toFixed(2)),
              isArrearClearance: isArrearSubject
            });
          }
          
          if (validationError) break;

          // 2. Arrear Subjects (Dynamic columns ending with '(Arrear)')
          const rowKeys = Object.keys(row);
          for (let k = 0; k < rowKeys.length; k++) {
            const key = rowKeys[k];
            if (key.includes('(Arrear)')) {
              const val = row[key];
              if (val !== undefined && val !== '') {
                const code = key.split(' ')[0];
                
                if (seenSubjectCodes.has(code)) {
                  validationError = `❌ Duplicate subject code found (Arrear Conflict) | Student: ${studentId} | Subject: ${code}`;
                  break;
                }
                seenSubjectCodes.add(code);
                
                const vaultEntry = student.activeArrears?.find(a => a.subjectCode === code);
                const creds = vaultEntry ? vaultEntry.credits : 3;
                
                let pts = 0;
                let isFail = false;
                const strVal = String(val).trim().toUpperCase();

                if (method === 'slab') {
                  if (gradeMapping[strVal] === undefined && !arrearGrades.includes(strVal)) {
                    validationError = `❌ Invalid/Unconfigured Grade "${val}" | Student: ${studentId} | Subject: ${code}. Please add it to Grade Mapping first!`;
                    break;
                  }
                  pts = getPointsFromGrade(strVal);
                  isFail = isFailure(strVal);
                } else {
                  const marks = Number(val);
                  if (isNaN(marks) || marks < 0 || marks > 100) {
                    validationError = `❌ Invalid Marks "${val}" (Must be 0-100) | Student: ${studentId} | Subject: ${code}`;
                    break;
                  }
                  if (method === 'continuous') {
                    pts = marks > 10 ? marks / 10 : marks;
                    isFail = pts < 5;
                  } else if (method === 'equal') {
                    pts = marks;
                    isFail = pts < 50;
                  }
                }

                totalCreditsAttempted += (method === 'equal' ? 1 : creds);
                if (!isFail) {
                  totalPointsEarned += Number((pts * (method === 'equal' ? 1 : creds)).toFixed(2));
                  clearedArrears.push(code);
                  if (status === 'Pass') status = 'Arrear Cleared';
                } else {
                  status = 'Has Arrears';
                }

                studentSubjects.push({
                  code: code,
                  name: vaultEntry ? vaultEntry.subjectName : code,
                  credits: creds,
                  grade: strVal,
                  pointsEarned: Number((pts * (method === 'equal' ? 1 : creds)).toFixed(2)),
                  isArrearClearance: true
                });
              }
            }
          }
          
          if (validationError) break;

          if (studentSubjects.length === 0) {
            validationError = `❌ No subjects found for student | Student: ${studentId}`;
            break;
          }

          let sgpa = totalCreditsAttempted > 0 ? (totalPointsEarned / totalCreditsAttempted) : 0;
          sgpa = Math.round(sgpa * 100) / 100;

          // Simple CGPA simulation for preview (just averages current CGPA with new SGPA)
          // In reality backend will calculate accurately if needed, but this is a good estimate
          const latestRecord = student.academicRecords && student.academicRecords.length > 0 
            ? student.academicRecords[student.academicRecords.length - 1] 
            : null;
          
          const prevCgpa = latestRecord && typeof latestRecord.cgpa === 'number' && !isNaN(latestRecord.cgpa) ? latestRecord.cgpa : 0;
          const newCgpa = prevCgpa > 0 ? ((prevCgpa + sgpa) / 2) : sgpa;

          parsed.push({
            studentId: student._id, // Mongo ID for backend
            smaartId: student.studentId, // For display
            name: student.fullName,
            sgpa,
            cgpa: Math.round(newCgpa * 100) / 100,
            status,
            subjects: studentSubjects,
            clearedArrears
          });
        } // End of For Loop
        
        if (validationError) {
          toast.error(validationError, { duration: 5000, style: { minWidth: '400px' } });
          setParsedResults([]); // Clear results so they can't save
          return;
        }

        setParsedResults(parsed);
        toast.success("File processed successfully! Please review the results.");
      } catch (err) {
        console.error(err);
        toast.error("Failed to parse the file. Please use the generated template.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length) processFile(files[0]);
  };

  const handleSaveToDatabase = async () => {
    try {
      setIsSaving(true);
      const payload = {
        batch: selectedBatchForUpload.batch,
        semester,
        method,
        collegeId: selectedCollegeId,
        records: parsedResults
      };

      const { data } = await api.post('/colleges/upload-cgpa', payload);
      
      if (data.success) {
        toast.success(`Successfully uploaded records for ${data.data.successful} students!`);
        // Reset everything to go back
        setWizardStep(1);
        setParsedResults(null);
        setSelectedBatchForUpload(null);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to save records');
    } finally {
      setIsSaving(false);
    }
  };

  const generateTranscript = (student) => {
    if (!student || !student.academicRecords) {
      toast.error("No academic records found for this student.");
      return;
    }
    
    // Sort records chronologically
    const sortedRecords = [...student.academicRecords].sort((a, b) => a.semester - b.semester);
    
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(26, 56, 132); // #1a3884 Deep Navy Blue
    doc.text("OFFICIAL ACADEMIC TRANSCRIPT", pageWidth / 2, 50, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(85, 85, 85);
    doc.text(`Issued by ${selectedCollege?.collegeName || 'SMAART Institute'}`, pageWidth / 2, 70, { align: 'center' });
    
    // Divider
    doc.setDrawColor(26, 56, 132);
    doc.setLineWidth(2);
    doc.line(40, 85, pageWidth - 40, 85);

    // Student Info Block
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text("STUDENT NAME", 40, 110);
    doc.text("SMAART ID", 200, 110);
    doc.text("PROGRAM", 350, 110);
    doc.text("BATCH", pageWidth - 40, 110, { align: 'right' });

    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text(student.fullName.toUpperCase(), 40, 125);
    doc.text(student.studentId || 'N/A', 200, 125);
    
    const programStr = selectedBatchForView?.degree || 'N/A';
    const splitProgram = doc.splitTextToSize(programStr, 150);
    doc.text(splitProgram, 350, 125);
    
    doc.text(selectedBatchForView?.batch || 'N/A', pageWidth - 40, 125, { align: 'right' });

    let currentY = Math.max(125 + (splitProgram.length * 12), 145);
    let totalCreditsEarned = 0;

    // Semesters
    sortedRecords.forEach(record => {
      totalCreditsEarned += (record.earnedCredits || 0);
      
      currentY += 20;
      if (currentY > 750) {
        doc.addPage();
        currentY = 50;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(26, 56, 132);
      doc.text(`Semester ${record.semester}`, 40, currentY);
      
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text(`SGPA: ${record.sgpa?.toFixed(2)}  |  Credits: ${record.totalCredits}`, pageWidth - 40, currentY, { align: 'right' });

      const tableData = record.subjects.map(sub => {
        const gradeStr = sub.grade?.toUpperCase();
        const isFail = ['RA', 'U', 'F', 'AB', 'W'].includes(gradeStr);
        return [
          sub.code,
          sub.name,
          sub.credits.toString(),
          sub.grade,
          isFail ? 'Fail' : (sub.isArrearClearance ? 'Cleared' : 'Pass')
        ];
      });

      autoTable(doc, {
        startY: currentY + 10,
        head: [['Subject Code', 'Subject Name', 'Credits', 'Grade', 'Status']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 5 },
        columnStyles: {
          2: { halign: 'center' },
          3: { halign: 'center', fontStyle: 'bold' },
          4: { halign: 'center' }
        },
        willDrawCell: function (data) {
          if (data.section === 'body' && (data.column.index === 3 || data.column.index === 4)) {
            const status = data.row.raw[4];
            if (status === 'Fail') {
              doc.setTextColor(220, 38, 38); // Red for fails
            } else if (status === 'Cleared') {
              doc.setTextColor(21, 128, 61); // Green for cleared arrears
            }
          }
        },
        margin: { left: 40, right: 40 }
      });

      currentY = doc.lastAutoTable.finalY + 10;
    });

    const finalCgpa = sortedRecords[sortedRecords.length - 1]?.cgpa || 0;
    const isGraduated = student.activeArrears?.length === 0 && finalCgpa > 0;

    // Summary Box
    currentY += 20;
    if (currentY > 700) {
      doc.addPage();
      currentY = 50;
    }

    doc.setFillColor(26, 56, 132);
    doc.roundedRect(40, currentY, pageWidth - 80, 70, 5, 5, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    doc.text("CUMULATIVE CGPA", pageWidth / 4, currentY + 25, { align: 'center' });
    doc.text("TOTAL CREDITS", pageWidth / 2, currentY + 25, { align: 'center' });
    doc.text("ACADEMIC STATUS", (pageWidth * 3) / 4, currentY + 25, { align: 'center' });

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(finalCgpa.toFixed(2), pageWidth / 4, currentY + 50, { align: 'center' });
    doc.text(totalCreditsEarned.toString(), pageWidth / 2, currentY + 50, { align: 'center' });
    doc.text(isGraduated ? 'Good Standing' : (student.activeArrears?.length > 0 ? 'Backlogs' : 'In Progress'), (pageWidth * 3) / 4, currentY + 50, { align: 'center' });

    currentY += 120;
    if (currentY > 750) {
      doc.addPage();
      currentY = 50;
    }

    // Signatures
    doc.setDrawColor(50, 50, 50);
    doc.setLineWidth(1);
    doc.line(60, currentY, 200, currentY);
    doc.line(pageWidth - 200, currentY, pageWidth - 60, currentY);

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(10);
    doc.text("Date of Issue", 130, currentY + 15, { align: 'center' });
    doc.setFont("helvetica", "normal");
    doc.text(new Date().toLocaleDateString(), 130, currentY + 30, { align: 'center' });

    doc.setFont("helvetica", "bold");
    doc.text("Controller of Examinations", pageWidth - 130, currentY + 15, { align: 'center' });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text("This is a system generated official transcript. For verification, contact the administration office.", pageWidth / 2, 810, { align: 'center' });
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 40, 810, { align: 'right' });
    }

    doc.save(`Transcript_${student.studentId || student.fullName.replace(/\s+/g, '_')}.pdf`);
    toast.success("Transcript downloaded successfully!");
  };


  const exportFilteredDataToExcel = (filteredStudents) => {
    if (!filteredStudents || filteredStudents.length === 0) {
      toast.error("No students to export.");
      return;
    }

    // --- SHEET 1: SUMMARY REPORT (1 Row per Student) ---
    const summaryData = [];
    let maxSem = 0;
    
    // Find maximum semester logged across all filtered students
    filteredStudents.forEach(student => {
      student.academicRecords?.forEach(r => {
        if (r.semester > maxSem) maxSem = r.semester;
      });
    });

    const summaryHeaders = [
      'Program', 
      'Batch', 
      'Student Name', 
      'SMAART ID'
    ];

    if (semesterFilter === 'all') {
      for (let i = 1; i <= maxSem; i++) {
        summaryHeaders.push(`Sem ${i} SGPA`);
      }
      summaryHeaders.push('Cumulative CGPA');
    } else {
      summaryHeaders.push(`Sem ${semesterFilter} SGPA`);
      summaryHeaders.push(`Sem ${semesterFilter} CGPA`);
    }
    
    summaryHeaders.push('Active Backlogs', 'Overall Status');
    summaryData.push(summaryHeaders);

    // --- SHEET 2: DETAILED SUBJECTS (1 Row per Subject) ---
    const detailedData = [];
    detailedData.push([
      'SMAART ID',
      'Student Name',
      'Department',
      'Batch',
      'Semester',
      'Subject Code',
      'Credits',
      'Grade',
      'Points',
      'Pass/Fail',
      'SGPA',
      'Cum. CGPA',
      'Overall Arrears'
    ]);

    filteredStudents.forEach(student => {
      // Logic for Summary Sheet
      let displayRecord = null;
      if (student.academicRecords && student.academicRecords.length > 0) {
        if (semesterFilter !== 'all') {
          displayRecord = student.academicRecords.find(r => r.semester === Number(semesterFilter));
        }
        if (!displayRecord) {
          displayRecord = student.academicRecords[student.academicRecords.length - 1];
        }
      }
      
      let backlogCount = 0;
      if (semesterFilter !== 'all' && displayRecord) {
        backlogCount = displayRecord.subjects.filter(s => ['RA', 'U', 'F', 'AB', 'W'].includes(s.grade?.toUpperCase())).length;
      } else {
        backlogCount = student.activeArrears?.length || 0;
      }

      const status = backlogCount > 0 ? (semesterFilter !== 'all' ? 'Arrears in Sem' : 'Backlogs Pending') : 'All Clear';
      const program = selectedBatchForView?.degree || 'N/A';
      const batch = student.batch || selectedBatchForView?.batch || 'N/A';

      const rowData = [
        program,
        batch,
        student.fullName,
        student.studentId || 'N/A'
      ];

      if (semesterFilter === 'all') {
        for (let i = 1; i <= maxSem; i++) {
          const semRec = student.academicRecords?.find(r => r.semester === i);
          rowData.push(semRec?.sgpa ? semRec.sgpa.toFixed(2) : '-');
        }
        rowData.push(displayRecord?.cgpa ? displayRecord.cgpa.toFixed(2) : '-');
      } else {
        rowData.push(displayRecord?.sgpa ? displayRecord.sgpa.toFixed(2) : '-');
        rowData.push(displayRecord?.cgpa ? displayRecord.cgpa.toFixed(2) : '-');
      }

      rowData.push(backlogCount.toString(), status);
      summaryData.push(rowData);

      // Logic for Detailed Sheet
      let recordsToExport = [];
      if (semesterFilter !== 'all') {
        const rec = student.academicRecords?.find(r => r.semester === Number(semesterFilter));
        if (rec) recordsToExport.push(rec);
      } else if (student.academicRecords && student.academicRecords.length > 0) {
        recordsToExport = student.academicRecords;
      }

      const dept = student.department?.fullName || student.department?.domain || 'N/A';

      recordsToExport.forEach(rec => {
        rec.subjects.forEach(sub => {
          const isFail = ['RA', 'U', 'F', 'AB', 'W'].includes(sub.grade?.toUpperCase()) || (sub.pointsEarned === 0 && sub.grade !== 'W');
          const passFail = isFail ? 'Fail' : 'Pass';

          detailedData.push([
            student.studentId || 'N/A',
            student.fullName,
            dept,
            batch,
            `Sem ${rec.semester}`,
            sub.code,
            sub.credits?.toString() || '-',
            sub.grade,
            sub.pointsEarned?.toString() || '0',
            passFail,
            rec.sgpa ? rec.sgpa.toFixed(2) : '-',
            rec.cgpa ? rec.cgpa.toFixed(2) : '-',
            backlogCount.toString()
          ]);
        });
      });
    });

    const wb = XLSX.utils.book_new();

    // Create Summary Sheet
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [
      { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 20 }, 
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary_Report");

    // Create Detailed Sheet
    const wsDetailed = XLSX.utils.aoa_to_sheet(detailedData);
    wsDetailed['!cols'] = [
      { wch: 20 }, { wch: 30 }, { wch: 35 }, { wch: 15 }, { wch: 12 }, 
      { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, 
      { wch: 10 }, { wch: 12 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, wsDetailed, "Detailed_Subjects");
    
    const fileName = `Academic_Records_Batch_${selectedBatchForView?.batch}${semesterFilter !== 'all' ? `_Sem${semesterFilter}` : ''}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast.success("Data exported to Excel successfully!");
  };

  // --- RENDERING ---
  const renderFilterBar = () => {
    const availableSemesters = [...new Set(viewStudents.flatMap(s => s.academicRecords?.map(r => r.semester) || []))].sort((a,b)=>a-b);
    return (
      <div className="p-5 border-b border-gray-100 bg-white flex flex-col gap-4">
        <div className="flex items-center gap-2 text-[#1a3884]">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          <p className="text-[15px] font-bold text-[#344054]">Filters & Search</p>
        </div>
        <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
          <div className="relative w-full xl:w-[320px] flex-shrink-0">
            <input
              type="text"
              placeholder="Search by name or SMAART ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-[#1a3884]/20 focus:border-[#1a3884] bg-[#fcfcfc] transition-all outline-none"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <div className="flex flex-wrap gap-3 w-full xl:w-auto xl:justify-end items-center">
            <select
              value={semesterFilter}
              onChange={(e) => setSemesterFilter(e.target.value)}
              className="bg-[#fcfcfc] border border-gray-200 text-[#344054] text-sm font-medium rounded-xl focus:ring-2 focus:ring-[#1a3884]/20 focus:border-[#1a3884] px-4 py-2 outline-none cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <option value="all">All Semesters</option>
              {availableSemesters.map(sem => (
                <option key={sem} value={sem}>Semester {sem}</option>
              ))}
            </select>
            <select
              value={cgpaFilter}
              onChange={(e) => setCgpaFilter(e.target.value)}
              className="bg-[#fcfcfc] border border-gray-200 text-[#344054] text-sm font-medium rounded-xl focus:ring-2 focus:ring-[#1a3884]/20 focus:border-[#1a3884] px-4 py-2 outline-none cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <option value="all">All CGPAs</option>
              <option value="9+">9.0 &amp; Above</option>
              <option value="8-9">8.0 – 8.99</option>
              <option value="7-8">7.0 – 7.99</option>
              <option value="6-7">6.0 – 6.99</option>
              <option value="<6">Below 6.0</option>
            </select>
            <select
              value={backlogFilter}
              onChange={(e) => setBacklogFilter(e.target.value)}
              className="bg-[#fcfcfc] border border-gray-200 text-[#344054] text-sm font-medium rounded-xl focus:ring-2 focus:ring-[#1a3884]/20 focus:border-[#1a3884] px-4 py-2 outline-none cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <option value="all">All Students</option>
              <option value="clear">✓ All Clear</option>
              <option value="backlogs">⚠ Has Arrears</option>
            </select>
            <div className="w-px h-8 bg-gray-200 mx-1 hidden xl:block"></div>
            <button
              onClick={() => exportFilteredDataToExcel(filteredStudents)}
              className="flex items-center gap-2 px-4 py-2 bg-[#107c41] text-white text-sm font-bold rounded-xl hover:bg-[#0c5e31] transition-colors shadow-sm"
              title="Export to Excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBatchSelection = () => {
    if (loading) {
      return (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884]"></div>
        </div>
      );
    }

    if (!selectedCollege) {
      return (
        <div className="text-center p-12 bg-white dark:bg-[#002147] rounded-xl border border-gray-200 dark:border-[#1a3884]/30 shadow-sm">
          <BookOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Institution Selected</h3>
          <p className="text-gray-500 dark:text-gray-400">Please contact support if you believe this is an error.</p>
        </div>
      );
    }

    const grouped = getGroupedDepartments(selectedCollege.departments);

    return (
      <div className="space-y-4">
        {grouped.length === 0 ? (
          <div className="text-center p-12 bg-white dark:bg-[#002147] rounded-xl border border-gray-200 dark:border-[#1a3884]/30 shadow-sm">
            <Layers className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Batches Found</h3>
            <p className="text-gray-500 dark:text-gray-400">Add departments and batches in Institution Settings first.</p>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group._id} className="bg-white dark:bg-[#002147] rounded-xl border border-gray-200 dark:border-[#1a3884]/30 shadow-sm overflow-hidden transition-all duration-200 hover:border-gray-300 dark:hover:border-[#1a3884]/50">
              <button
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50/50 dark:bg-[#001736]/50 hover:bg-gray-50 dark:hover:bg-[#001f40] transition-colors"
                onClick={() => toggleGroup(group._id)}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1a3884]/10 dark:bg-[#4f7dff]/10 rounded-lg">
                    <BookOpen className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-black text-[#1a3884] dark:text-[#4f7dff] flex items-center gap-3">
                      {group.friendlyName}
                      {group.duration && group.duration !== 'N/A' && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider bg-blue-50 text-[#1a3884] border border-[#1a3884]/20">
                          {String(group.duration).replace(/years?/i, '').trim()} Year Course
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-500">
                    {group.batches.length} Batch{group.batches.length !== 1 ? 'es' : ''}
                  </span>
                  {expandedGroups[group._id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {expandedGroups[group._id] && (
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
                  {group.batches.length > 0 ? (
                    group.batches.map((batch, index) => (
                      <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white dark:bg-[#001f40] border border-gray-100 dark:border-gray-700 rounded-lg gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-1.5 h-8 bg-[#1a3884] dark:bg-[#4f7dff] rounded-full"></div>
                          <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              Batch: <span className="text-[#1a3884] dark:text-[#4f7dff]">{batch}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            className="flex items-center gap-2 px-4 py-2 border-[#1a3884] text-[#1a3884] hover:bg-[#1a3884]/5 rounded-lg text-sm font-semibold transition-all"
                            onClick={() => handleViewRecords(group.friendlyName, batch, group._id || group.degreeId)}
                          >
                            <Users className="w-4 h-4" />
                            View Records
                          </Button>
                          <Button
                            variant="primary"
                            className="flex items-center gap-2 px-4 py-2 bg-[#1a3884] hover:bg-[#1a3884]/90 text-white rounded-lg text-sm font-semibold transition-all shadow-sm"
                            onClick={() => setSelectedBatchForUpload({ degree: group.friendlyName, batch, duration: group.duration, deptId: group._id || group.degreeId })}
                          >
                            <Upload className="w-4 h-4" />
                            Upload CGPA
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No active batches available for this degree.</p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  const renderSetupWizard = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSelectedBatchForUpload(null)}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h2 className="text-2xl font-black text-[#1a3884] dark:text-white tracking-tight">Configure Semester Details</h2>
              <p className="text-sm text-gray-500 mt-1">Define subjects and generate the Excel template for Batch {selectedBatchForUpload.batch}</p>
            </div>
          </div>
          <button 
            onClick={() => setShowHowItWorks(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-[#1a3884] bg-[#1a3884]/5 border border-[#1a3884]/20 rounded-lg hover:bg-[#1a3884]/10 transition-colors shadow-sm"
          >
            <Info className="w-4 h-4" /> How It Works
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Target Semester</label>
            <select 
              className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a3884]"
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
            >
              {(() => {
                let max = 8;
                if (selectedBatchForUpload?.duration) {
                  const d = String(selectedBatchForUpload.duration).toLowerCase();
                  if (d.includes('1')) max = 2;
                  if (d.includes('2')) max = 4;
                  if (d.includes('3')) max = 6;
                  if (d.includes('4')) max = 8;
                  if (d.includes('5')) max = 10;
                }
                const options = [];
                for (let i = 1; i <= max; i++) {
                  const year = Math.ceil(i / 2);
                  options.push(<option key={i} value={i}>Year {year} - Semester {i}</option>);
                }
                return options;
              })()}
            </select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Calculation Method</label>
              {method === 'slab' && (
                <button
                  onClick={() => setShowGradeMappingModal(true)}
                  className="flex items-center gap-1.5 text-xs font-bold text-[#1a3884] hover:text-blue-700 transition-colors bg-[#1a3884]/5 px-2 py-1 rounded-md"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Configure Points
                </button>
              )}
            </div>
            <select 
              className="w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a3884]"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="slab">Slab-Based (e.g., Anna University)</option>
              <option value="continuous">Continuous (e.g., Madras University)</option>
              <option value="equal">Equal-Credit (Autonomous)</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <div>
              <h3 className="text-lg font-bold text-[#1a3884]">Subject Configuration</h3>
              <p className="text-sm text-gray-500">Define the subjects for this semester.</p>
            </div>
            <button 
              className="flex items-center gap-2 px-3 py-1.5 bg-[#1a3884]/5 hover:bg-[#1a3884]/10 border border-[#1a3884]/20 rounded-lg text-[#1a3884] font-bold text-xs transition-colors shadow-sm"
              onClick={() => setShowSmartPaste(!showSmartPaste)}
            >
              {showSmartPaste ? (
                <>
                  <X className="w-3.5 h-3.5" /> Cancel Quick Import
                </>
              ) : (
                <>
                  <Layers className="w-3.5 h-3.5" /> Quick Import Subjects
                </>
              )}
            </button>
          </div>
          <div className="p-4 space-y-4">
            {showSmartPaste ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Copy a table of subjects (Code | Name | Credits) from ChatGPT, Excel, or a PDF and paste it below.</p>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a3884] h-32 font-mono"
                  placeholder="Example:&#10;R21UCE971 | Development of Smart Cities | 3.0&#10;R21UGS531 | Reasoning and Aptitude | 1.0"
                  value={smartPasteText}
                  onChange={(e) => setSmartPasteText(e.target.value)}
                />
                <Button variant="primary" className="bg-[#1a3884]" onClick={handleSmartPaste}>
                  Parse & Add Subjects
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 px-4 pb-2 border-b border-gray-100 mb-2">
                  <div className="w-1/3 text-xs font-bold text-gray-500 uppercase tracking-wider">Subject Code</div>
                  <div className="flex-1 text-xs font-bold text-gray-500 uppercase tracking-wider">Subject Name</div>
                  {method !== 'equal' && (
                    <div className="w-24 text-xs font-bold text-gray-500 uppercase tracking-wider">Credits</div>
                  )}
                  {subjects.length > 1 && <div className="w-9"></div>}
                </div>
                {subjects.map((sub, idx) => (
                  <div key={idx} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                    <input 
                      type="text" placeholder="Code (e.g. CS8391)" 
                      className="w-1/3 p-2 border rounded text-sm"
                      value={sub.code} onChange={(e) => handleSubjectChange(idx, 'code', e.target.value)}
                    />
                    <input 
                      type="text" placeholder="Subject Name" 
                      className="flex-1 p-2 border rounded text-sm"
                      value={sub.name} onChange={(e) => handleSubjectChange(idx, 'name', e.target.value)}
                    />
                    {method !== 'equal' && (
                      <input 
                        type="number" placeholder="Credits" 
                        min="0" step="0.5"
                        className="w-24 p-2 border rounded text-sm"
                        value={sub.credits} 
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val !== '' && Number(val) < 0) return;
                          handleSubjectChange(idx, 'credits', val);
                        }}
                      />
                    )}
                    {subjects.length > 1 && (
                      <button onClick={() => removeSubject(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={addSubject} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-[#1a3884] bg-[#1a3884]/5 hover:bg-[#1a3884]/10 rounded-lg transition-colors border border-[#1a3884]/10 shadow-sm">
                  <Plus className="w-4 h-4" /> Add Subject
                </button>
              </>
            )}
          </div>
        </div>

        <Card title="Arrear Management">
          <label className="flex items-center gap-3 p-4 border rounded-lg bg-[#1a3884]/5 cursor-pointer hover:bg-[#1a3884]/10 transition-colors">
            <input 
              type="checkbox" 
              checked={includeArrears} 
              onChange={(e) => setIncludeArrears(e.target.checked)}
              className="w-5 h-5 text-[#1a3884] rounded focus:ring-[#1a3884]"
            />
            <div>
              <p className="font-bold text-gray-900">Include Pending Arrears (Auto-Injector)</p>
              <p className="text-xs text-gray-500">Automatically appends columns for any pending backlogs this batch has.</p>
            </div>
          </label>
        </Card>

        <div className="pt-4 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={() => setSelectedBatchForUpload(null)}>Cancel</Button>
          <Button variant="primary" onClick={generateTemplate} className="bg-[#1a3884]">
            <Download className="w-4 h-4 mr-2" />
            Generate Excel Template
          </Button>
        </div>
      </div>
    );
  };

  const renderUploadAndPreview = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => { setWizardStep(1); setParsedResults(null); }}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-black text-[#1a3884] dark:text-white tracking-tight">Upload & Review</h2>
              <p className="text-sm text-gray-500 mt-1">Upload the filled template to calculate CGPA instantly.</p>
            </div>
          </div>
        </div>

        {!parsedResults ? (
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
              isDragging ? 'border-[#1a3884] bg-[#1a3884]/5 scale-[1.02]' : 'border-gray-300 hover:border-[#1a3884]/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FileSpreadsheet className="w-16 h-16 mx-auto text-[#1a3884] mb-4 opacity-80" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Completed Template</h3>
            <p className="text-gray-500 mb-6">Drag and drop your Excel/CSV file here, or click to browse.</p>
            
            <input
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={(e) => {
                if (e.target.files?.length) processFile(e.target.files[0]);
              }}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#1a3884] text-white rounded-lg font-semibold cursor-pointer hover:bg-[#1a3884]/90 transition-colors shadow-sm"
            >
              <Upload className="w-5 h-5 mr-2" />
              Browse Files
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border">
              <div>
                <p className="text-sm text-gray-500">Records Processed</p>
                <p className="text-2xl font-black text-[#1a3884]">{parsedResults.length}</p>
              </div>
              <Button variant="primary" onClick={handleSaveToDatabase} disabled={isSaving} className="bg-[#1a3884]">
                {isSaving ? 'Saving...' : 'Confirm & Save to Database'}
              </Button>
            </div>

            <Card title="Live Calculation Preview">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Student</th>
                      <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">SMAART ID</th>
                      <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">SGPA</th>
                      <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Est. CGPA</th>
                      <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedResults.map((res, idx) => (
                      <tr key={idx} className={res.subjects.length === 0 ? 'bg-yellow-50/50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3 font-semibold text-sm">{res.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{res.smaartId}</td>
                        <td className="px-4 py-3 font-bold">{res.sgpa.toFixed(2)}</td>
                        <td className="px-4 py-3 font-bold text-[#1a3884]">{res.cgpa.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {res.subjects.length === 0 ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded">Data Missing</span>
                          ) : res.status === 'Arrear Cleared' ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">1 Arrear Cleared!</span>
                          ) : res.status === 'Has Arrears' ? (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">Has Arrears</span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded">Pass</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  };



  const renderViewRecords = () => {
    // Determine available semesters across all students in the batch
    const availableSemesters = [...new Set(viewStudents.flatMap(s => s.academicRecords?.map(r => r.semester) || []))].sort((a,b)=>a-b);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSelectedBatchForView(null)}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-black text-[#1a3884] dark:text-white tracking-tight">
                Academic Records - Batch {selectedBatchForView.batch}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{selectedBatchForView.degree}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {renderFilterBar()}

          {loadingStudents ? (
             <div className="flex justify-center p-12">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884]"></div>
             </div>
          ) : filteredStudents.length === 0 ? (
             <div className="text-center p-12 text-gray-500">
               <p className="font-bold text-gray-700 mb-1">No students found.</p>
               <p className="text-sm">Try adjusting your search or filters.</p>
             </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Student Profile</th>
                    <th className="px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">SMAART ID</th>
                    <th className="px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">{semesterFilter === 'all' ? 'Cum. CGPA' : `Sem ${semesterFilter} CGPA`}</th>
                    <th className="px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">{semesterFilter === 'all' ? 'Latest SGPA' : `Sem ${semesterFilter} SGPA`}</th>
                    <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-500 uppercase tracking-wider">Active Backlogs</th>
                    <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-500 uppercase tracking-wider">Upto Semester</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.map((student, idx) => {
                    let displayRecord = null;
                    if (student.academicRecords && student.academicRecords.length > 0) {
                      if (expandedStudentId === student._id && activeSemesterTab) {
                        displayRecord = student.academicRecords.find(r => r.semester === activeSemesterTab);
                      } else if (semesterFilter !== 'all') {
                        displayRecord = student.academicRecords.find(r => r.semester === Number(semesterFilter));
                      }
                      if (!displayRecord) {
                        displayRecord = student.academicRecords[student.academicRecords.length - 1];
                      }
                    }
                    
                    // Dynamically change the styling if we are showing a specific semester
                    const isShowingSpecific = (expandedStudentId === student._id) || (semesterFilter !== 'all');

                    return (
                    <React.Fragment key={idx}>
                      <tr 
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedStudentId === student._id ? 'bg-[#1a3884]/5' : ''}`}
                        onClick={() => {
                          if (expandedStudentId === student._id) {
                            setExpandedStudentId(null);
                            setActiveSemesterTab(null);
                          } else {
                            setExpandedStudentId(student._id);
                            if (student.academicRecords?.length > 0) {
                              const maxSem = Math.max(...student.academicRecords.map(r => r.semester));
                              setActiveSemesterTab(maxSem);
                            } else {
                              setActiveSemesterTab(null);
                            }
                          }
                        }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1a3884] text-white flex items-center justify-center font-bold text-lg uppercase shadow-sm">
                              {student.fullName?.charAt(0) || '?'}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-bold text-sm text-gray-900 capitalize">{student.fullName}</span>
                              <span className="text-xs text-gray-500">Batch {selectedBatchForView.batch}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-600">{student.studentId}</td>
                        <td className="px-4 py-3">
                          <span className={`font-bold text-[15px] ${isShowingSpecific ? 'text-[#1a3884]' : 'text-gray-900'}`}>
                            {displayRecord?.cgpa ? displayRecord.cgpa.toFixed(2) : (student.cgpa ? Number(student.cgpa).toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-bold text-[15px] ${isShowingSpecific ? 'text-gray-900' : 'text-gray-700'}`}>
                            {displayRecord?.sgpa ? displayRecord.sgpa.toFixed(2) : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            {(() => {
                              if (!student.academicRecords || student.academicRecords.length === 0) {
                                return (
                                  <span className="px-2.5 py-1 bg-gray-50 text-gray-500 border border-gray-200 text-xs font-bold rounded-full flex items-center gap-1.5 w-max transition-colors">
                                    <Activity className="w-3.5 h-3.5" />
                                    No Records
                                  </span>
                                );
                              }

                              let backlogCount = 0;
                              if (isShowingSpecific && displayRecord) {
                                backlogCount = displayRecord.subjects.filter(s => ['RA', 'U', 'F', 'AB', 'W'].includes(s.grade?.toUpperCase())).length;
                              } else {
                                backlogCount = student.activeBacklogs !== undefined 
                                  ? student.activeBacklogs 
                                  : (student.academic?.activeBacklogs !== undefined 
                                      ? student.academic.activeBacklogs 
                                      : (Array.isArray(student.activeArrears) ? student.activeArrears.length : 0));
                              }

                              return backlogCount > 0 ? (
                                <span className="px-2.5 py-1 bg-red-50 text-red-600 border border-red-100 text-xs font-bold rounded-full flex items-center gap-1.5 w-max transition-colors">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  {backlogCount} {isShowingSpecific ? `Arrear${backlogCount === 1 ? '' : 's'} (Sem)` : `Arrear${backlogCount === 1 ? '' : 's'}`}
                                </span>
                              ) : (
                                <span className="px-2.5 py-1 bg-green-50 text-green-700 border border-green-100 text-xs font-bold rounded-full flex items-center gap-1.5 w-max transition-colors">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  0 (Cleared)
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-600">
                          <div className="flex flex-col items-center justify-center">
                            {student.academicRecords && student.academicRecords.length > 0 ? (
                              <>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-[#1a3884] border border-indigo-100 shadow-sm">
                                  <Layers className="w-3.5 h-3.5 text-[#1a3884]" />
                                  Upto Sem {Math.max(...student.academicRecords.map(r => r.semester))}
                                </span>
                                <span className="text-[10px] text-gray-400 font-semibold mt-0.5">
                                  {student.academicRecords.length} {student.academicRecords.length === 1 ? 'Sem Logged' : 'Sems Logged'}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400 font-semibold">No Records</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedStudentId === student._id ? 'rotate-180 text-[#1a3884]' : ''}`} />
                        </td>
                      </tr>
                      {expandedStudentId === student._id && (
                        <tr>
                          <td colSpan="7" className="p-0 border-b border-gray-100">
                            <div className="bg-[#1a3884]/5 p-6 space-y-6 shadow-inner">
                              <div className="flex items-center justify-between border-b pb-2">
                                <h3 className="text-lg font-black text-[#1a3884]">Academic History: {student.fullName}</h3>
                                <div className="flex items-center gap-3">
                                  {student.academicRecords && student.academicRecords.length > 0 && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); generateTranscript(student); }}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a3884] text-white text-xs font-bold rounded-lg shadow hover:bg-[#0f2459] transition-colors uppercase tracking-wider"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text w-3.5 h-3.5"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/></svg>
                                      Download Transcript
                                    </button>
                                  )}
                                  {student.academicRecords && student.academicRecords.length > 0 && (
                                    <select 
                                      value={activeSemesterTab || ''} 
                                      onChange={(e) => setActiveSemesterTab(Number(e.target.value))}
                                      className="bg-white border border-gray-300 text-gray-700 text-sm font-bold rounded-lg focus:ring-[#1a3884] focus:border-[#1a3884] block px-3 py-1.5 shadow-sm"
                                    >
                                      {[...student.academicRecords].sort((a, b) => b.semester - a.semester).map(r => (
                                        <option key={r.semester} value={r.semester}>Semester {r.semester}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              </div>
                              {(() => {
                                if (!student.academicRecords) return null;
                                const failGrades = ['RA', 'U', 'F', 'AB', 'W'];
                                const timelineEvents = [];
                                
                                const chronRecords = [...student.academicRecords].sort((a,b) => a.semester - b.semester);
                                const failures = {}; 
                                
                                chronRecords.forEach(record => {
                                  record.subjects.forEach(sub => {
                                    const grade = sub.grade?.toUpperCase();
                                    if (failGrades.includes(grade)) {
                                      if (!failures[sub.code]) {
                                        failures[sub.code] = { sem: record.semester, grade };
                                      }
                                    } else if (failures[sub.code]) {
                                      timelineEvents.push({
                                        code: sub.code,
                                        name: sub.name,
                                        failedSem: failures[sub.code].sem,
                                        failedGrade: failures[sub.code].grade,
                                        clearedSem: record.semester,
                                        clearedGrade: grade
                                      });
                                      delete failures[sub.code];
                                    }
                                  });
                                });

                                if (timelineEvents.length === 0) return null;

                                return (
                                  <div className="mb-6 bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                                    <h4 className="text-sm font-black text-[#1a3884] mb-4 uppercase tracking-wider flex items-center gap-2">
                                      <ShieldCheck className="w-4 h-4" />
                                      Arrear Clearance History
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {timelineEvents.map((event, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-[#fcfdfd] hover:bg-gray-50 transition-colors shadow-sm">
                                          <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check w-4 h-4"><path d="M20 6 9 17l-5-5"/></svg>
                                            </div>
                                            <div>
                                              <p className="text-xs font-bold text-[#1a3884]">{event.code}</p>
                                              <p className="text-[10px] font-semibold text-gray-500 truncate max-w-[150px]" title={event.name}>{event.name}</p>
                                            </div>
                                          </div>
                                          <div className="text-right flex items-center gap-2 bg-white px-2 py-1.5 rounded-md border border-gray-100">
                                            <div className="text-right">
                                              <span className="text-[10px] font-bold text-red-400 line-through block">Sem {event.failedSem} • {event.failedGrade}</span>
                                            </div>
                                            <div className="text-gray-300">
                                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right w-3 h-3"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            </div>
                                            <div className="text-left">
                                              <span className="text-[11px] font-black text-emerald-600 block">Sem {event.clearedSem} • {event.clearedGrade}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}

                              {student.academicRecords && student.academicRecords.length > 0 ? (
                                [...student.academicRecords].filter(record => record.semester === activeSemesterTab).map((record, rIdx) => {
                                    const isEditing = editingRecord?.studentId === student._id && editingRecord?.semester === record.semester;
                                    const currentRecord = isEditing ? editingRecord : record;
                                    let displaySgpa = currentRecord.sgpa;
                                    if (isEditing) {
                                      let tCredits = 0; let tPoints = 0;
                                      currentRecord.subjects.forEach(s => { tCredits += s.credits; tPoints += s.pointsEarned || 0; });
                                      displaySgpa = tCredits > 0 ? (tPoints / tCredits) : 0;
                                    }
                                    return (
                                  <div key={rIdx} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${isEditing ? 'border-[#1a3884] ring-1 ring-[#1a3884]/20' : 'border-[#1a3884]/10'}`}>
                                    <div className={`flex items-center justify-between p-4 border-b border-gray-100 ${isEditing ? 'bg-blue-50/50' : 'bg-gray-50'}`}>
                                      <div className="flex items-center gap-3">
                                        <h4 className="font-black text-sm tracking-wide text-gray-900 uppercase">Semester {record.semester}</h4>
                                        {!isEditing ? (
                                          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded shadow-sm ${record.status === 'Pass' || record.status === 'Arrear Cleared' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                            {record.status === 'Result Pending' ? 'Has Arrears' : record.status}
                                          </span>
                                        ) : (
                                          <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded shadow-sm bg-blue-100 text-blue-700 border border-blue-200 animate-pulse">
                                            Editing
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex gap-6 items-center">
                                        <div className="text-gray-500 font-bold text-[11px] uppercase tracking-wider">Credits <span className="text-gray-900 font-black ml-1 text-[13px]">{record.earnedCredits}/{record.totalCredits}</span></div>
                                        <div className="text-gray-500 font-bold text-[11px] uppercase tracking-wider">SGPA <span className="text-[#1a3884] font-black ml-1 text-[13px]">{displaySgpa?.toFixed(2)}</span></div>
                                        {!isEditing && (
                                          <div className="text-gray-500 font-bold text-[11px] uppercase tracking-wider">CGPA <span className="text-[#1a3884] font-black ml-1 text-[13px]">{record.cgpa?.toFixed(2)}</span></div>
                                        )}
                                        {isEditing ? (
                                          <div className="flex gap-2 ml-2">
                                            <button 
                                              onClick={(e) => { e.stopPropagation(); setEditingRecord(null); }}
                                              className="px-3 py-1 bg-white border border-gray-200 hover:bg-gray-100 transition-colors rounded text-gray-700 font-bold text-[10px] uppercase tracking-wider shadow-sm"
                                            >
                                              Cancel
                                            </button>
                                            <button 
                                              onClick={(e) => { e.stopPropagation(); handleSaveSemester(); }}
                                              disabled={savingGrades}
                                              className="px-3 py-1 bg-[#1a3884] hover:bg-[#001736] transition-colors rounded text-white font-bold text-[10px] uppercase tracking-wider shadow-sm disabled:opacity-70 flex items-center gap-1.5"
                                            >
                                              {savingGrades ? 'Saving...' : (
                                                <>
                                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle w-3 h-3"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
                                                  Save
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        ) : (
                                          <button 
                                            onClick={(e) => { e.stopPropagation(); handleEditSemester(student._id, record); }}
                                            className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 hover:bg-gray-100 transition-colors rounded text-gray-700 font-bold text-[10px] uppercase tracking-wider shadow-sm ml-2"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit2 w-3 h-3 text-gray-500"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                                            Edit
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="p-0 overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-gray-50/80 border-b border-gray-100">
                                          <tr>
                                            <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase text-[11px] tracking-wider">Code</th>
                                            <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase text-[11px] tracking-wider">Subject Name</th>
                                            <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase text-[11px] tracking-wider">Credits</th>
                                            <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase text-[11px] tracking-wider">Grade</th>
                                            <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase text-[11px] tracking-wider">Points Earned</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {currentRecord.subjects.map((sub, sIdx) => {
                                              const gradeStr = sub.grade?.toUpperCase();
                                              const isFail = ['RA', 'U', 'F', 'AB', 'W'].includes(gradeStr);
                                              
                                              // Dynamically detect if this was an arrear that is now cleared
                                              let isArrearCleared = sub.isArrearClearance || false;
                                              if (!isArrearCleared && !isFail && student.academicRecords) {
                                                const pastSemesters = student.academicRecords.filter(r => r.semester < currentRecord.semester);
                                                isArrearCleared = pastSemesters.some(r => 
                                                  r.subjects.some(s => s.code === sub.code && ['RA', 'U', 'F', 'AB', 'W'].includes(s.grade?.toUpperCase()))
                                                );
                                              }

                                              return (
                                            <tr key={sIdx} className={`transition-colors ${isArrearCleared ? 'bg-green-50/50' : 'hover:bg-gray-50'}`}>
                                              <td className="px-4 py-3 font-bold text-xs text-gray-700">{sub.code}</td>
                                              <td className="px-4 py-3 text-[13px] text-gray-800 font-medium">
                                                {sub.name}
                                                {isArrearCleared && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Cleared Arrear</span>}
                                              </td>
                                              <td className="px-4 py-3 text-center text-gray-600 text-[13px] font-medium">{sub.credits}</td>
                                              <td className="px-4 py-3 text-center">
                                                {isEditing ? (
                                                    <input 
                                                      type="text" 
                                                      value={sub.grade || ''} 
                                                      onChange={(e) => handleGradeChange(sub.code, e.target.value)}
                                                      className="w-16 px-2 py-1 text-center border border-gray-300 rounded font-bold text-[13px] text-[#1a3884] focus:outline-none focus:border-[#1a3884] focus:ring-1 focus:ring-[#1a3884] uppercase"
                                                      maxLength={2}
                                                    />
                                                  ) : (
                                                  <div className="flex items-center justify-center gap-1.5">
                                                    <span className={`text-[13px] font-bold ${isFail ? 'text-red-600' : 'text-[#1a3884]'}`}>
                                                      {sub.grade}
                                                    </span>
                                                    {sub.editedBy && (
                                                      <div className="group relative flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield-check w-3.5 h-3.5 text-blue-400 cursor-help hover:text-blue-600 transition-colors"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-max max-w-xs bg-gray-900 text-white text-[10px] font-bold px-2.5 py-1.5 rounded shadow-lg z-50">
                                                          Manually updated by {sub.editedBy} on {new Date(sub.editedAt).toLocaleDateString()}
                                                          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                  )}
                                              </td>
                                              <td className="px-4 py-3 text-center text-[13px] font-bold text-gray-900">
                                                {typeof sub.pointsEarned === 'number' ? Number(sub.pointsEarned.toFixed(2)) : sub.pointsEarned}
                                              </td>
                                            </tr>
                                          )})}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )})
                              ) : (
                                <div className="text-center p-8 bg-white rounded-xl border border-dashed border-gray-300">
                                  <p className="text-gray-500 font-medium">No detailed semester records found for this student.</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        {!selectedBatchForUpload && !selectedBatchForView && (
          <div className="bg-white dark:bg-[#002147] rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-[#1a3884]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="p-2.5 bg-gray-50 dark:bg-[#001736] hover:bg-gray-100 dark:hover:bg-[#001f40] rounded-xl transition-colors border border-gray-200 dark:border-[#1a3884]/30">
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-black text-[#1a3884] dark:text-white tracking-tight">Academic Records</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
                  <FileSpreadsheet className="w-4 h-4" /> Manage and bulk upload student academic records by batch
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        {selectedBatchForView ? (
          renderViewRecords()
        ) : !selectedBatchForUpload ? (
          <Card title="Manage Academic Records by Batch">
            <div className="mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Select a batch below to upload or view student academic records (CGPA/Semesters).</p>
            </div>
            {renderBatchSelection()}
          </Card>
        ) : wizardStep === 1 ? (
          <Card>{renderSetupWizard()}</Card>
        ) : (
          renderUploadAndPreview()
        )}
      </div>

      {/* How It Works Modal */}
      {showHowItWorks && (
        <div className="fixed inset-y-0 right-0 left-0 lg:left-64 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 text-[#1a3884]">
                <Info className="w-5 h-5" />
                <h3 className="text-lg font-black tracking-tight">System Documentation: Grading & Arrears</h3>
              </div>
              <button 
                onClick={() => setShowHowItWorks(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Calculation Methods */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-[#1a3884]" /> 
                  SGPA/CGPA Calculation Methods
                </h4>
                <div className="space-y-3 text-sm text-gray-600 ml-6">
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <p className="font-bold text-gray-800">1. Slab-Based (e.g., Anna University)</p>
                    <p className="mt-1">Uses standardized letter grades translated to a 10-point scale: <span className="font-semibold">O=10, A+=9, A=8, B+=7, B=6, C=5</span>. Any grade below 'C' (like RA, U) triggers a fail status and logs 0 points.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <p className="font-bold text-gray-800">2. Continuous Assessment (e.g., Madras University)</p>
                    <p className="mt-1">Uses direct numerical marks instead of letters. Divides raw marks by 10 to obtain grade points (e.g., <span className="font-semibold">87 Marks = 8.7 Points</span>). Marks below 50 are considered failed.</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <p className="font-bold text-gray-800">3. Equal-Credit (Autonomous)</p>
                    <p className="mt-1">Disregards individual subject credit weightings. Every subject is treated identically to calculate a straight average of the grade points earned.</p>
                  </div>
                </div>
              </div>

              {/* Arrear Management */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-900 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#1a3884]" /> 
                  Intelligent Arrear Vault (Auto-Injector)
                </h4>
                <div className="text-sm text-gray-600 ml-6 space-y-2">
                  <p>When you enable <span className="font-bold text-[#1a3884]">"Include Pending Arrears"</span>, the system automatically cross-references every student in this batch against the central Arrear Vault.</p>
                  <p>If a student failed a subject in a previous semester, the system will dynamically inject a custom column (e.g., <span className="font-mono bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-xs text-red-600 font-bold">CS8391 (Arrear)</span>) into their row in the Excel template.</p>
                  <p>Upon uploading the new semester results, if a passing grade is detected in that arrear column, the system automatically removes the subject from their active backlog, updates their cumulative status, and badges it as <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-bold">Cleared Arrear</span> in their history.</p>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button 
                onClick={() => setShowHowItWorks(false)}
                className="px-6 py-2 bg-[#1a3884] text-white font-bold text-sm rounded-lg hover:bg-[#001736] transition-colors shadow-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Grade Mapping Modal */}
      {showGradeMappingModal && (
        <div className="fixed inset-y-0 right-0 left-0 lg:left-64 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-[#1a3884]/5">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-[#1a3884] rounded-md">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-black text-[#1a3884] text-base leading-none">Grade Mapping</h3>
                  <p className="text-[11px] text-gray-500 font-medium mt-0.5">Define points & arrears.</p>
                </div>
              </div>
              <button onClick={() => setShowGradeMappingModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-blue-50 border border-blue-100 p-2.5 rounded-md flex gap-2 text-xs text-blue-800 leading-snug">
                <Info className="w-4 h-4 shrink-0 text-blue-600" />
                <p>Customize point multipliers for Standard Grades. Define Arrear grades explicitly below.</p>
              </div>
              
              {/* Standard Grades Section */}
              <div className="space-y-2.5">
                <h4 className="font-bold text-[#1a3884] border-b pb-1.5 text-xs uppercase tracking-wider">Standard Passing Grades</h4>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {Object.entries(gradeMapping).map(([grade, points], idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <div className="flex-1 flex items-center bg-white border border-gray-200 rounded text-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#1a3884]/20 focus-within:border-[#1a3884]">
                        <div className="px-2 py-1 bg-gray-50 font-bold text-gray-700 text-xs border-r border-gray-200 w-10 text-center select-none">
                          {grade}
                        </div>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.5"
                          value={points}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setGradeMapping(prev => ({...prev, [grade]: val}));
                          }}
                          className="w-full px-1.5 py-1 text-xs font-bold focus:outline-none text-center text-[#1a3884]"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const newMapping = {...gradeMapping};
                          delete newMapping[grade];
                          setGradeMapping(newMapping);
                        }}
                        className="text-gray-300 hover:text-red-500 p-1 rounded transition-colors"
                        title="Remove Grade"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-1.5 border-dashed border-gray-300 text-gray-500 hover:text-[#1a3884] hover:border-[#1a3884] text-[11px] font-bold py-1.5"
                  onClick={() => {
                    const newGrade = prompt("Enter new passing grade letter (e.g., S):");
                    if (newGrade && !gradeMapping[newGrade.toUpperCase()] && !arrearGrades.includes(newGrade.toUpperCase())) {
                      setGradeMapping(prev => ({...prev, [newGrade.toUpperCase()]: 10}));
                    }
                  }}
                >
                  <Plus className="w-3 h-3" /> Add Passing Grade
                </Button>
              </div>

              {/* Arrear Grades Section */}
              <div className="space-y-2.5 pt-2">
                <div className="flex items-center justify-between border-b pb-1.5">
                  <h4 className="font-bold text-red-600 text-xs uppercase tracking-wider">Arrear / Special (0 pts)</h4>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {arrearGrades.map((grade, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-red-50 border border-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold shadow-sm">
                      {grade}
                      <button 
                        onClick={() => setArrearGrades(prev => prev.filter(g => g !== grade))}
                        className="text-red-400 hover:text-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newGrade = prompt("Enter new arrear/special grade (e.g., Z):");
                      if (newGrade && !arrearGrades.includes(newGrade.toUpperCase()) && gradeMapping[newGrade.toUpperCase()] === undefined) {
                        setArrearGrades(prev => [...prev, newGrade.toUpperCase()]);
                      }
                    }}
                    className="flex items-center gap-1 bg-gray-50 border border-dashed border-gray-300 text-gray-500 px-2 py-1 rounded text-xs font-bold hover:text-red-600 hover:border-red-600 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
              </div>

            </div>
            <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-end">
              <Button variant="primary" onClick={() => setShowGradeMappingModal(false)} className="bg-[#1a3884] text-xs py-1.5 px-4 font-bold">
                Save & Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadCGPA;
